import React, { useState } from 'react'
import axios from 'axios'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { toast } from 'sonner'
import { KaryvaLogoAnimated3D } from '../components/KaryvaLogoAnimated3D'
import { useTranslation } from '../i18n/I18nContext'
import { userInvitationsService } from '../services/userInvitations'

/**
 * Sprint 32 — public invitation-accept page, reached from the emailed link
 * (`/accept-invitation?token=…`). The invitee sets a password to create their
 * account; on success they're sent to /login.
 *
 * Error contract: 404 = unknown/already-used token; 400 = expired token; both
 * collapse to one generic "link is invalid or expired" message (no
 * enumeration). 422 = backend password policy (rendered inline).
 */
export default function AcceptInvitationPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [policyErrors, setPolicyErrors] = useState<string[]>([])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPolicyErrors([])
    if (password !== confirm) {
      toast.error(t('auth.accept.mismatch'))
      return
    }
    if (password.length < 8) {
      toast.error(t('auth.accept.tooShort'))
      return
    }
    setLoading(true)
    try {
      await userInvitationsService.accept(token, password)
      toast.success(t('auth.accept.success'))
      navigate('/login')
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined
      if (status === 422 && detail?.password_errors) {
        setPolicyErrors(detail.password_errors)
      } else if (status === 400 || status === 404) {
        toast.error(t('auth.accept.invalidLink'))
      } else {
        toast.error(t('auth.accept.genericError'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#102a43] via-[#1a3a52] to-[#102a43] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <KaryvaLogoAnimated3D size="header" darkBg={true} />
        </div>

        <div className="bg-[#1d293d] rounded-[14px] border border-[#314158] p-6 md:p-8 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.3)]">
          {!token ? (
            <div className="space-y-4 text-center">
              <h1 className="font-['Arial:Regular',sans-serif] text-xl text-white">
                {t('auth.accept.invalidLink')}
              </h1>
              <p className="font-['Arial:Regular',sans-serif] text-sm leading-[22px] text-[#cad5e2]">
                {t('auth.accept.missingToken')}
              </p>
              <Link
                to="/login"
                className="inline-block text-sm text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
              >
                {t('auth.accept.backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2 text-center">
                <h1 className="font-['Arial:Regular',sans-serif] text-xl text-white">
                  {t('auth.accept.title')}
                </h1>
                <p className="font-['Arial:Regular',sans-serif] text-sm leading-[22px] text-[#cad5e2]">
                  {t('auth.accept.subtitle')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block" htmlFor="accept-password">
                    <span className="font-['Arial:Regular',sans-serif] text-sm text-[#cad5e2]">
                      {t('auth.accept.passwordLabel')}
                    </span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Lock className="size-4 text-[#62748e]" />
                    </div>
                    <input
                      id="accept-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      autoFocus
                      className="w-full bg-[rgba(15,23,43,0.5)] h-[40px] rounded-[8px] pl-10 pr-3 py-2 font-['Arial:Regular',sans-serif] text-sm text-white placeholder-[#62748e] border border-[#45556c] focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block" htmlFor="accept-confirm">
                    <span className="font-['Arial:Regular',sans-serif] text-sm text-[#cad5e2]">
                      {t('auth.accept.confirmLabel')}
                    </span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Lock className="size-4 text-[#62748e]" />
                    </div>
                    <input
                      id="accept-confirm"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full bg-[rgba(15,23,43,0.5)] h-[40px] rounded-[8px] pl-10 pr-3 py-2 font-['Arial:Regular',sans-serif] text-sm text-white placeholder-[#62748e] border border-[#45556c] focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                {policyErrors.length > 0 && (
                  <ul className="space-y-1 rounded-[8px] border border-[#7f1d1d] bg-[rgba(127,29,29,0.15)] p-3">
                    {policyErrors.map((err) => (
                      <li
                        key={err}
                        className="font-['Arial:Regular',sans-serif] text-xs leading-[18px] text-[#fca5a5]"
                      >
                        {err}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#fe9a00] to-[#e17100] h-[44px] rounded-[8px] shadow-[0px_10px_15px_-3px_rgba(123,51,6,0.3)] hover:from-[#e58900] hover:to-[#c86600] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <span className="font-['Arial:Regular',sans-serif] text-sm text-[#0f172b] font-medium">
                  {loading ? t('auth.accept.submitting') : t('auth.accept.submit')}
                </span>
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
                >
                  {t('auth.accept.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
